import React, { useState } from 'react'
import { PersistedQuestion } from '../questionTypes'
import { SurveyResponses, SurveyAnswer, toSurveyAnswers, findMissingRequiredIds, isDebouncedSaveType } from './surveyResponses'
import { useDebouncedSaves } from './useDebouncedSaves'
import { QuestionRenderer } from './QuestionRenderer'

interface SurveyResponseFormProps {
  questions: PersistedQuestion[]
  // Fires only when the respondent submits AND every required question is answered.
  // The caller's handler does the actual POST / reCAPTCHA / sets submitted_at.
  onSubmit: (answers: SurveyAnswer[]) => void
  // Optional progressive-save hook: fires on every answer change so the caller can
  // persist as the form is filled in. Omit it for submit-only contexts.
  onResponseChange?: (questionId: number, value: string, allAnswers: SurveyAnswer[]) => void
  // Pre-populate fields from a previously saved response.
  initialAnswers?: SurveyResponses
  // Debounce (ms) applied to progressive saves for free-entry types (see
  // DEBOUNCED_SAVE_TYPES) so typing doesn't hit the server every keystroke. Discrete
  // picks always save immediately. 0 (default) = save on every change.
  saveDebounceMs?: number
  // The caller's submit is async (POST + reCAPTCHA); pass true to disable the button.
  submitting?: boolean
  submitLabel?: string
  hideSubmitButton?: boolean
}

export const SurveyResponseForm: React.FC<SurveyResponseFormProps> = ({
  questions,
  onSubmit,
  onResponseChange,
  initialAnswers = {},
  saveDebounceMs = 0,
  submitting = false,
  submitLabel = 'Submit',
  hideSubmitButton = false
}) => {
  const [answers, setAnswers] = useState<SurveyResponses>(initialAnswers)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const { schedule, flush } = useDebouncedSaves(saveDebounceMs)

  const missingRequired = findMissingRequiredIds(questions, answers)

  const handleAnswerChange = (question: PersistedQuestion, value: string): void => {
    const updated = { ...answers, [question.id]: value }
    setAnswers(updated)
    if (onResponseChange == null) return
    const save = (): void => onResponseChange(question.id, value, toSurveyAnswers(updated))
    if (saveDebounceMs > 0 && isDebouncedSaveType(question.question_type)) {
      schedule(question.id, save)
    } else {
      save()
    }
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    // Persist any debounced save still in flight before we submit.
    flush()
    setSubmitAttempted(true)
    if (missingRequired.length > 0) return
    onSubmit(toSurveyAnswers(answers))
  }

  return (
    <form className='survey-response-form' onSubmit={handleSubmit}>
      {questions.map((question, index) => (
        <QuestionRenderer
          key={question.id}
          question={question}
          questionNumber={index + 1}
          value={answers[question.id] ?? ''}
          onChange={(value) => handleAnswerChange(question, value)}
        />
      ))}

      {submitAttempted && missingRequired.length > 0 && (
        <p className='survey-response-form__error'>Please answer all required questions.</p>
      )}

      {!hideSubmitButton && (
        <div className='survey-response-form__submit'>
          <button type='submit' className='survey-response-form__submit-button' disabled={submitting}>
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      )}
    </form>
  )
}
